import Vuex from 'vuex'
import Vue from 'vue'
Vue.use(Vuex);
import VuexPersistence from 'vuex-persist'
import { error } from 'pdf-lib';

/**
 * PERSISTENCE PATTERN (using vuex-persist):
 * 1. Add property to module state
 * 2. Create setter mutation (no localStorage needed)
 * 3. Add property to vuexLocal reducer if it should be persisted
 *
 * vuex-persist automatically syncs state to localStorage
 */

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
  reducer: (state) => ({
    // User module state
    user: {
      username: state.user.username,
      token: state.user.token,
      userId: state.user.userId,
      userEmail: state.user.userEmail,
      canEdit: state.user.canEdit
    },
    // App module state
    app: {
      userGeminiApiKey: state.app.userGeminiApiKey,
      enabledSearchTypes: state.app.enabledSearchTypes,
      dictionaryFilters: state.app.dictionaryFilters,
      collapsedDictionarySections: state.app.collapsedDictionarySections,
      translationToolIgnoreList: state.app.translationToolIgnoreList,
      ignoreStandardParticles: state.app.ignoreStandardParticles,
      particleCategoriesEnabled: state.app.particleCategoriesEnabled,
      autoShowFilteredResults: state.app.autoShowFilteredResults,
      hasBeenAskedAboutAutoShow: state.app.hasBeenAskedAboutAutoShow,
      showCustomNotes: state.app.showCustomNotes,
      showWylieInDefiniendum: state.app.showWylieInDefiniendum,
      showSanskritInDefiniendum: state.app.showSanskritInDefiniendum,
      enableAiTranslation: state.app.enableAiTranslation,
      translationToolLayout: state.app.translationToolLayout,
      compactResultsView: state.app.compactResultsView,
      searchType: state.app.searchType,
      searchTypeText: state.app.searchTypeText,
      searchTypeHistory: state.app.searchTypeHistory,
      containsOrBeginsWith: state.app.containsOrBeginsWith,
      colloquial: state.app.colloquial,
      showRecentSearchTypes: state.app.showRecentSearchTypes,
      hasSeenTutorial: state.app.hasSeenTutorial,
      hasSeenHideTermDialog: state.app.hasSeenHideTermDialog
    }
  })
})

// User module - handles authentication
const userModule = {
  namespaced: true,
  state: {
    username: null,
    userId: null,
    userEmail: null,
    token: null,
    canEdit: false
  },
  mutations: {
    loginUser(state, {username, token, id, email, canEdit}) {
      state.username = username;
      state.token = token;
      state.userEmail = email;
      state.userId = id;
      state.canEdit = canEdit;
      Vue.$log.info("Setting user info: ", {username, token, email, userId:id});
    },
    logoutUser(state) {
      state.username = null;
      state.token = null;
      state.userEmail = null;
      state.userId = null;
      state.canEdit = false;
    }
  }
};

// Default values for resettable preferences
const defaultPreferences = {
  enabledSearchTypes: {
    fulltext: true,
    'fulltext-all-fields': true,
    definiendum: true,
    englishDefinition: true,
    definiendumStarts: true,
    definiendumExact: true,
    definiendumLike: false,
    definiendumRegex: false,
    'translation-tool': true,
    'verb-tense': true,
    'monlam-englishToTibetan': true,
    'monlam-tibetanToEnglish': true,
    'monlam-tibetanToTibetan': true,
    'monlam-tibetanToTibetanOld': false
  },
  dictionaryFilters: {
    normalSearch: {
      umaTibet: true,
      hackettIndex: true,
      verbinator: true,
      rangjung: true,
      berzin: true,
      eightFourThousand: true,
      tshigMdzod: true,
      dungDkar: true,
      dagTshig: true,
      tsepakRigdzin: true,
    },
    translationTool: {
      umaTibet: true,
      hackettIndex: true,
      verbinator: true,
      rangjung: true,
      berzin: true,
      eightFourThousand: true,
      tshigMdzod: true,
      dungDkar: true,
      dagTshig: true,
      tsepakRigdzin: true,
    }
  },
  translationToolIgnoreList: [],
  ignoreStandardParticles: false,
  autoShowFilteredResults: true,
  hasBeenAskedAboutAutoShow: false,
  enableAiTranslation: true,
  showSanskritInDefiniendum: true,
  showWylieInDefiniendum: true,
  showCustomNotes: true,
  collapsedDictionarySections: {},
  showRecentSearchTypes: false,
  hasSeenTutorial: false,
  hasSeenHideTermDialog: false,
  particleCategoriesEnabled: {
    genitive: true,
    agentive: true,
    connective: true,
    nominalizers: true,
    negatives: true,
    sentenceEnding: true,
    question: true,
    laDon: true
  },
  searchText: '',
  searchTypeText: 'Defined Term (begins with)',
  searchType: 'definiendumStarts',
  containsOrBeginsWith: 'beginsWith', // used in the monlam searches
  searchTypeHistory: [], // Track recently used search types
  showWylie: false,
  compactResultsView: false,
  translationToolTextAreaFontSize: "1.7rem",
  colloquial: "no",
  userGeminiApiKey: null,
  translationToolLayout: 'side-by-side', // 'stacked' or 'side-by-side'
  standardParticles: [
    {
      id: 'genitive',
      name: 'Genitive Particles',
      description: 'Possessive case markers (similar to "of" or "\'s" in English)',
      enabled: true,
      particles: ['ཀྱི་', 'གི་', 'གྱི་', 'ཡི་', 'ཀྱིན་', 'གྱིན་']
    },
    {
      id: 'agentive',
      name: 'Agentive/Instrumental Particles',
      description: 'Mark the agent or instrument performing an action',
      enabled: true,
      particles: ['ཀྱིས་', 'གིས་', 'གྱིས་', 'ཡིས་']
    },
    {
      id: 'connective',
      name: 'Connective Particles',
      description: 'Link clauses, phrases, or indicate location/time',
      enabled: true,
      particles: ['དང་', 'ནི་', 'ནས་', 'ལ་', 'ན་', 'ཏེ་', 'དེ་', 'སྟེ་', 'ཅིང་', 'ཞིང་', 'ཀྱང་']
    },
    {
      id: 'nominalizers',
      name: 'Nominalizers',
      description: 'Convert verbs or adjectives into nouns',
      enabled: true,
      particles: ['པ་', 'པོ་', 'པས་', 'བ་', 'བོ་']
    },
    {
      id: 'negatives',
      name: 'Negatives',
      description: 'Negation markers',
      enabled: true,
      particles: ['མ་', 'མི་']
    },
    {
      id: 'sentenceEnding',
      name: 'Sentence Ending Particles',
      description: 'Mark the end of statements',
      enabled: true,
      particles: ['ནོ་', 'སོ་', 'ཏོ་', 'དོ་', 'རོ་', 'འོ་']
    },
    {
      id: 'question',
      name: 'Question Particles',
      description: 'Mark interrogative sentences',
      enabled: true,
      particles: ['འམ་', 'སམ་', 'དམ་', 'ཏམ་']
    },
    {
      id: 'laDon',
      name: 'La Don Particles',
      description: 'Directional/locative case markers',
      enabled: true,
      particles: ['སུ་', 'རུ་', 'ཏུ་', 'དུ་', 'ན་', 'ར་', 'ལ་']
    }
  ]
};

// App module - handles application preferences and search state
const appModule = {
  namespaced: true,
  state: {
    cypressTesting: false,
    allowEdits: false,
    editInProcess: false,
    editToken: "551946e0-4093-4697-b4c5-87d25a0b15ab",
    ...defaultPreferences,
    
  },
  mutations: {
    setSearchText(state, val) {
      state.searchText = val;
    },
    setSearchTypeText(state, val) {
      state.searchTypeText = val;
    },
    setSearchType(state, val) {
      state.searchType = val;
    },
    setShowWylie(state, val) {
      state.showWylie = val;
    },
    setSearchTypeHistory(state, val) {
      state.searchTypeHistory = val
    },
    setShowRecentSearchTypes(state,val) {
      state.showRecentSearchTypes = val
    },
    addSearchTypeToHistory(state, val) {
      console.log("addSearchTypeToHistory: " + val)
      // not tracking colloquial search types
      if (val.includes("monlam")) return;
  
      let history = state.searchTypeHistory;
      const index = history.indexOf(val);
      if (index > -1) {
        history.splice(index, 1); // Remove if already exists
      }
      history.unshift(val); // Add to beginning
      if (history.length > 10) {
        history.pop(); // Keeping 10, but only four will be shown, see: recentSearchTypes()
      }
      history = history.filter( v => !v.includes('monlam'))
      state.searchTypeHistory = history;
      console.log(`addSearchTypeToHistory: ${val} added to history:`,state.searchTypeHistory)
    },
    setShowCustomNotes(state, val) {
      state.showCustomNotes = val;
    },
    setCompactResultsView(state, val) {
      state.compactResultsView = val;
    },
    setContainsOrBeginsWith(state, val){
      state.containsOrBeginsWith = val
    },
    toggleDictionarySection(state, sectionName) {
      Vue.set(state.collapsedDictionarySections, sectionName, !state.collapsedDictionarySections[sectionName]);
    },
    setTranslationToolTextAreaFontSize(state, fontSize) {
      state.translationToolTextAreaFontSize = fontSize;
    },
    setDictionaryFilters(state, filters) {
      state.dictionaryFilters = filters;
    },
    setEnabledSearchTypes(state, searchTypes) {
      state.enabledSearchTypes = searchTypes;
    },
    addToTranslationToolIgnoreList(state, tibetanTerm) {
      if (!state.translationToolIgnoreList.includes(tibetanTerm)) {
        state.translationToolIgnoreList.push(tibetanTerm);
      }
    },
    removeFromTranslationToolIgnoreList(state, tibetanTerm) {
      const index = state.translationToolIgnoreList.indexOf(tibetanTerm);
      if (index > -1) {
        state.translationToolIgnoreList.splice(index, 1);
      }
    },
    setIgnoreStandardParticles(state, value) {
      state.ignoreStandardParticles = value;
    },
    setAutoShowFilteredResults(state, value) {
      state.autoShowFilteredResults = value;
    },
    setHasBeenAskedAboutAutoShow(state, value) {
      state.hasBeenAskedAboutAutoShow = value;
    },
    setEnableAiTranslation(state, value) {
      state.enableAiTranslation = value;
    },
    setUserGeminiApiKey(state, value) {
      state.userGeminiApiKey = value;
    },
    setShowWylieInDefiniendum(state, val) {
      state.showWylieInDefiniendum = val;
    },
    setShowSanskritInDefiniendum(state, val) {
      state.showSanskritInDefiniendum = val;
    },
    setParticleCategoryEnabled(state, { categoryId, enabled }) {
      Vue.set(state.particleCategoriesEnabled, categoryId, enabled);
    },
    setTranslationToolLayout(state, layout) {
      state.translationToolLayout = layout;
    },
    setColloquial(state,val) {
      if(val != 'yes' && val != 'no') {
        throw error("Illegal 'colloquial' value: " + val)
      }
      state.colloquial = val;
    },
    setHasSeenTutorial(state, val) {
      state.hasSeenTutorial = val;
    },
    setHasSeenHideTermDialog(state, val) {
      state.hasSeenHideTermDialog = val;
    },
    resetPreferences(state) {
      try {
        const resetValues = JSON.parse(JSON.stringify(defaultPreferences));
        Object.keys(resetValues).forEach(key => {
          state[key] = resetValues[key];
        });
        console.log('All preferences reset to default values');
      } catch (e) {
        console.error('Failed to reset preferences:', e);
      }
    }
  },
  getters: {
    activeDictionaries(state) {
      return {
        normalSearch: Object.keys(state.dictionaryFilters.normalSearch).filter(
          key => state.dictionaryFilters.normalSearch[key]
        ),
        translationTool: Object.keys(state.dictionaryFilters.translationTool).filter(
          key => state.dictionaryFilters.translationTool[key]
        )
      };
    }
  }
};

export default new Vuex.Store({
  modules: {
    user: userModule,
    app: appModule
  },
  plugins: [vuexLocal.plugin]
})
